/**
 * SessionValuesLine — every session that valued this item on one criterion.
 *
 * An item is valued many times over its life: a room scores it in Q4, a facilitator
 * types a number for it in a design review, Q1's cycle asks again. Each session keeps
 * its own values (canon 03 §2.7), and until now the item page showed none of them, so
 * the fourth cycle started from zero. This line reads `criterion_scores_by_session`
 * (migration 842) for the item and draws one entry per session: the session's
 * combined score, how many scored, when. An approver may make any of them the record,
 * through the same act that sets an official value (`set_accepted_value`, 962), with
 * the session named in the acceptance note.
 *
 * A strategic objective used as a criterion (1066) is scored in a session on the
 * session's range, but its record is a word of its own scale (None … Primary). For one,
 * each session's number is read as that word on the session's range, the word is what
 * becomes the record, and the item's link to the objective follows it.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isObjectiveCriterionKey, objectiveStrengthFromRange, type ScoringRange } from '@/lib/objectiveCriteria';
import { readToolSettings } from '@/lib/toolSettings';

interface ScoreRow {
  session_id: string;
  session_name: string | null;
  item_id: string;
  criterion_key: string;
  source: string;
  scorer_count: number | null;
  mean_score: number | string | null;
  scored_at: string | null;
}

// One read per item, shared by every criterion card on the page.
const cache = new Map<string, Promise<ScoreRow[]>>();

function rowsForItem(itemId: string): Promise<ScoreRow[]> {
  let p = cache.get(itemId);
  if (!p) {
    p = (async () => {
      try {
        const { getSafeSupabaseClient, isSupabaseAvailable } = await import('@/lib/supabaseSync');
        if (!isSupabaseAvailable()) return [];
        const supabase = getSafeSupabaseClient();
        if (!supabase) return [];
        const { data, error } = await supabase.rpc('criterion_scores_by_session', { p_item_id: itemId });
        if (error || !Array.isArray(data)) return [];
        return data as ScoreRow[];
      } catch {
        return [];
      }
    })();
    cache.set(itemId, p);
    // A read is good for the page's life, not for ever: let a later open re-read.
    setTimeout(() => cache.delete(itemId), 60_000);
  }
  return p;
}

export interface SessionValuesLineProps {
  itemId: string;
  attributeKey: string;
  /** The official value as drawn, to say when a session's number already is the record. */
  officialValue: string | null;
  canAccept: boolean;
  /** Make a session's number the record; the caller routes it through the one governed act. */
  onMakeRecord?: (value: string, note: string) => Promise<void>;
  /** The scale's allowed labels, when it has points; a session's mean that is not one of them cannot become the record as it is. */
  allowedLabels?: readonly string[];
}

function sourceWord(source: string): string {
  if (source === 'participants' || source === 'group') return 'the room';
  if (source === 'facilitator') return 'the facilitator';
  return source;
}

export function SessionValuesLine({ itemId, attributeKey, officialValue, canAccept, onMakeRecord, allowedLabels }: SessionValuesLineProps) {
  const [rows, setRows] = useState<ScoreRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void rowsForItem(itemId).then((all) => { if (!cancelled) setRows(all); });
    return () => { cancelled = true; };
  }, [itemId]);

  const mine = useMemo(() => {
    const list = (rows ?? []).filter((r) => r.criterion_key === attributeKey);
    // One entry per session: the room's number over the facilitator's when both exist.
    const bySession = new Map<string, ScoreRow>();
    for (const r of list) {
      const have = bySession.get(r.session_id);
      if (!have || (have.source === 'facilitator' && r.source !== 'facilitator')) bySession.set(r.session_id, r);
    }
    return [...bySession.values()].sort((a, b) => (b.scored_at ?? '').localeCompare(a.scored_at ?? ''));
  }, [rows, attributeKey]);

  // For an objective: each session's range, so its number can be read as the scale's word.
  const isObjective = isObjectiveCriterionKey(attributeKey);
  const sessionIdsKey = isObjective ? mine.map((r) => r.session_id).join(',') : '';
  const [ranges, setRanges] = useState<Record<string, ScoringRange>>({});
  useEffect(() => {
    if (!sessionIdsKey) return;
    let cancelled = false;
    void (async () => {
      try {
        const { getSafeSupabaseClient, isSupabaseAvailable } = await import('@/lib/supabaseSync');
        if (!isSupabaseAvailable()) return;
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        const { data } = await supabase.from('prioritization_sessions').select('id, tool_config').in('id', sessionIdsKey.split(','));
        if (cancelled || !Array.isArray(data)) return;
        const next: Record<string, ScoringRange> = {};
        for (const row of data as { id: string; tool_config: Record<string, unknown> | null }[]) {
          next[row.id] = readToolSettings(row.tool_config).scoringScale;
        }
        setRanges(next);
      } catch {
        // Without a range the number cannot be read as a word; the line still shows it.
      }
    })();
    return () => { cancelled = true; };
  }, [sessionIdsKey]);

  if (mine.length === 0) return null;

  const meanText = (r: ScoreRow) => {
    const n = typeof r.mean_score === 'number' ? r.mean_score : Number(r.mean_score);
    return Number.isFinite(n) ? String(Math.round(n * 10) / 10) : '—';
  };
  /** For an objective: the session's number as the scale's word on the session's range. */
  const objectiveWord = (r: ScoreRow) => {
    const n = typeof r.mean_score === 'number' ? r.mean_score : Number(r.mean_score);
    const range = ranges[r.session_id];
    return isObjective && range && Number.isFinite(n) ? objectiveStrengthFromRange(n, range) : null;
  };
  const recordable = (text: string) => !allowedLabels || allowedLabels.length === 0 || allowedLabels.includes(text) || allowedLabels.includes(String(Math.round(Number(text))));
  const asLabel = (text: string) => (allowedLabels && allowedLabels.includes(text) ? text : allowedLabels && allowedLabels.includes(String(Math.round(Number(text)))) ? String(Math.round(Number(text))) : text);

  return (
    <div className="mt-2" data-testid={`session-values-${attributeKey}`}>
      <div className="text-[11px] font-semibold text-[var(--text-ter)] uppercase tracking-wide">In sessions</div>
      <ul className="mt-1 space-y-0.5">
        {mine.map((r) => {
          const text = meanText(r);
          const word = objectiveWord(r);
          const recordText = isObjective ? word : asLabel(text);
          const isRecord = officialValue != null && recordText === officialValue;
          const range = ranges[r.session_id];
          const when = r.scored_at ? new Date(r.scored_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
          const who = r.source === 'facilitator' ? 'the facilitator' : `${r.scorer_count ?? 1} ${(r.scorer_count ?? 1) === 1 ? 'person' : 'people'}`;
          return (
            <li key={r.session_id} className="flex flex-wrap items-baseline gap-x-2 text-xs text-[var(--text-sec)]">
              <span className="font-medium tabular-nums text-[var(--text-pri)]">{text}</span>
              {isObjective && word && range && (
                <span className="text-[var(--text-ter)]">of {range.min}–{range.max}, read as {word}</span>
              )}
              <span>
                in <Link to={`/prioritization/sessions/${r.session_id}`} className="underline">{r.session_name || 'a session'}</Link>
                {' '}({sourceWord(r.source)}{r.source !== 'facilitator' ? `, ${who}` : ''}{when ? `, ${when}` : ''})
              </span>
              {isRecord ? (
                <span className="text-[10px] text-[var(--green)]">the record</span>
              ) : canAccept && onMakeRecord && (isObjective ? !!word : recordable(text)) ? (
                <button
                  type="button"
                  disabled={busy === r.session_id}
                  onClick={async () => {
                    setBusy(r.session_id);
                    setError(null);
                    try {
                      await onMakeRecord(
                        recordText ?? asLabel(text),
                        `From ${r.session_name || 'a session'} (${sourceWord(r.source)}${r.source !== 'facilitator' ? `, ${who}` : ''}${isObjective && range ? `; ${text} of ${range.min}–${range.max}` : ''})`,
                      );
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Not saved');
                    } finally {
                      setBusy(null);
                    }
                  }}
                  className="text-[10px] underline text-[var(--text-ter)] hover:text-[var(--text-pri)] disabled:opacity-50"
                >
                  {busy === r.session_id ? 'Saving…' : 'Make this the record'}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {error && <p className="mt-1 text-[10px] text-[var(--coral)]">{error}</p>}
    </div>
  );
}
